import { useContext, useEffect } from 'react';
import { StoreContext } from '../context';
import {
  CallPresence,
  MeetPresenceStore,
  callPresenceOf,
} from '../stores/meetPresenceStore';
import { useWebSocketStore } from './useWebSocketStore';

/**
 * Whether someone can be called right now, as one answer.
 *
 * The two facts it is made of arrive by different routes and nothing joined them up: callability
 * rides the websocket's online-status updates, and who is on a call is an HTTP read from
 * los-video-service. The chat window had only the first, so it showed a green dot and a live
 * Huddle button for someone deep in a call; Meet and the mobile app, which had both, showed the
 * same person amber. Asking this instead of webSocketStore.isUserCallable is what keeps the
 * three surfaces saying one thing.
 *
 * @param enabled fetch on mount. Pass isElectronApp() where Meet cannot appear at all — the
 *        answer would be read by nobody and the request is a round trip per page load.
 */
export const useCallPresence = (enabled = true) => {
  const { meetPresenceStore } = useContext<{
    meetPresenceStore: MeetPresenceStore;
  }>(StoreContext);
  const { webSocketStore } = useWebSocketStore();

  useEffect(() => {
    if (!enabled) return;
    meetPresenceStore?.load();
  }, [enabled, meetPresenceStore]);

  /*
   * Reads both observables, so an `observer` calling this during render subscribes to both and
   * re-renders when either moves — someone closing the desktop app, or joining a call.
   */
  const presenceOf = (userId: number): CallPresence =>
    callPresenceOf(
      webSocketStore?.isUserCallable(userId) ?? false,
      meetPresenceStore?.isUserBusy(userId) ?? false,
    );

  return { presenceOf, meetPresenceStore };
};
